import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetSurveys, adminGetSurvey, adminCreateSurvey, adminUpdateSurvey, adminSendPendingSurveys } from '../../lib/adminApi';

const QUESTION_TYPES = [
  { value: 'rating_10',       label: 'Rating 1–10' },
  { value: 'rating_5',        label: 'Rating 1–5 (stars)' },
  { value: 'yes_no',          label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'open_text',       label: 'Open Text' },
];

const BLANK_Q = { question_text: '', question_type: 'rating_10', options: null, required: true };

const MC_TYPES = ['multiple_choice', 'multiple_choice_multi'];

const HELP_TIPS = [
  {
    icon: '⭐',
    title: 'Rating questions',
    body: 'Rating 1–10 is great for NPS ("How likely are you to recommend us?"). Rating 1–5 works well for satisfaction or quality scores. Answers are averaged automatically in Results.',
  },
  {
    icon: '✅',
    title: 'Yes / No',
    body: 'Simple binary questions. Good for "Did the file meet your expectations?" Use sparingly — one or two per survey max.',
  },
  {
    icon: '☑',
    title: 'Multiple Choice — single vs. multiple',
    body: 'Each option goes in its own field (use + Add Option). "Select one" shows radio buttons — respondent picks exactly one answer. "Select multiple" shows checkboxes — respondent can pick several. Example options: Much faster than expected / About what I expected / Slower than expected.',
  },
  {
    icon: '💬',
    title: 'Open Text',
    body: 'Free-form text box. Best placed at the end: "Any other comments?" Keep required off so it doesn\'t block submission.',
  },
  {
    icon: '📋',
    title: 'Survey tips',
    body: 'Keep surveys short — 5–7 questions max. Set status to Draft while building, then switch to Active when ready to send. Trigger days = how many days after a job completes before the survey is scheduled.',
  },
];

function HelpModal({ onClose }) {
  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0a2540', margin: 0 }}>Survey Builder Guide</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
        {HELP_TIPS.map((tip, i) => (
          <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 20, paddingBottom: 20, borderBottom: i < HELP_TIPS.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
            <span style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{tip.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0a2540', marginBottom: 4 }}>{tip.title}</div>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{tip.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionRow({ q, idx, onChange, onRemove, onMove, total }) {
  const isMC = MC_TYPES.includes(q.question_type);
  const isMulti = q.question_type === 'multiple_choice_multi';
  const options = Array.isArray(q.options) ? q.options : [];

  // Normalize dropdown value — multi variant still shows as "multiple_choice" in the select
  const selectValue = isMulti ? 'multiple_choice' : q.question_type;

  function handleTypeChange(val) {
    // Switching away from MC clears options; keep existing options when switching between MC variants
    if (!MC_TYPES.includes(val)) {
      onChange(idx, 'options', null);
    }
    onChange(idx, 'question_type', val);
  }

  return (
    <div style={s.qRow}>
      <div style={s.qMeta}>
        <span style={s.qNum}>{idx + 1}</span>
        <div style={s.qArrows}>
          <button onClick={() => onMove(idx, -1)} disabled={idx === 0} style={s.arrowBtn}>▲</button>
          <button onClick={() => onMove(idx, 1)} disabled={idx === total - 1} style={s.arrowBtn}>▼</button>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <input
          style={s.qInput}
          placeholder="Question text…"
          value={q.question_text}
          onChange={e => onChange(idx, 'question_text', e.target.value)}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <select style={s.qSelect} value={selectValue} onChange={e => handleTypeChange(e.target.value)}>
            {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          {isMC && (
            <div style={{ flex: 1, minWidth: 220 }}>
              {/* Single / Multiple toggle */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 8 }}>
                <button
                  onClick={() => onChange(idx, 'question_type', 'multiple_choice')}
                  style={{ ...s.toggleBtn, borderRadius: '5px 0 0 5px', background: !isMulti ? '#0176CE' : '#f1f5f9', color: !isMulti ? '#fff' : '#374151', fontWeight: !isMulti ? 700 : 400 }}
                >
                  ◉ Select one
                </button>
                <button
                  onClick={() => onChange(idx, 'question_type', 'multiple_choice_multi')}
                  style={{ ...s.toggleBtn, borderRadius: '0 5px 5px 0', borderLeft: 'none', background: isMulti ? '#0176CE' : '#f1f5f9', color: isMulti ? '#fff' : '#374151', fontWeight: isMulti ? 700 : 400 }}
                >
                  ☑ Select multiple
                </button>
              </div>

              {/* Options editor */}
              {options.map((opt, oi) => (
                <div key={oi} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                  <input
                    style={{ ...s.qInput, flex: 1 }}
                    placeholder={`Option ${oi + 1}`}
                    value={opt}
                    onChange={e => {
                      const opts = [...options];
                      opts[oi] = e.target.value;
                      onChange(idx, 'options', opts);
                    }}
                  />
                  <button
                    onClick={() => onChange(idx, 'options', options.filter((_, i) => i !== oi))}
                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: '2px 6px', flexShrink: 0 }}
                  >✕</button>
                </div>
              ))}
              <button
                onClick={() => onChange(idx, 'options', [...options, ''])}
                style={{ background: 'none', border: '1px dashed #cbd5e1', borderRadius: 5, color: '#6b7280', cursor: 'pointer', fontSize: 11, padding: '3px 10px' }}
              >+ Add Option</button>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexShrink: 0 }}>
            <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={q.required} onChange={e => onChange(idx, 'required', e.target.checked)} />
              Required
            </label>
            <button onClick={() => onRemove(idx)} style={s.removeBtn}>✕</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSurveys() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', trigger_days: 7, status: 'draft', questions: [{ ...BLANK_Q }]
  });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { const r = await adminGetSurveys(); setSurveys(r.surveys || []); }
    catch (e) { showToast('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3500); }

  function openNew() {
    setEditId(null);
    setForm({ title: '', description: '', trigger_days: 7, status: 'draft', questions: [{ ...BLANK_Q }] });
    setShowForm(true);
  }

  async function openEdit(survey) {
    try {
      const r = await adminGetSurvey(survey.survey_id);
      setEditId(survey.survey_id);
      setForm({ ...r.survey, questions: r.questions?.length ? r.questions : [{ ...BLANK_Q }] });
      setShowForm(true);
    } catch (e) { showToast('Error loading survey: ' + e.message); }
  }

  function changeQ(idx, field, val) {
    setForm(f => { const qs = [...f.questions]; qs[idx] = { ...qs[idx], [field]: val }; return { ...f, questions: qs }; });
  }
  function removeQ(idx) {
    setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  }
  function moveQ(idx, dir) {
    setForm(f => {
      const qs = [...f.questions];
      const to = idx + dir;
      if (to < 0 || to >= qs.length) return f;
      [qs[idx], qs[to]] = [qs[to], qs[idx]];
      return { ...f, questions: qs };
    });
  }

  async function save() {
    if (!form.title.trim()) { showToast('Title is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await adminUpdateSurvey(editId, form);
        showToast('Survey updated');
      } else {
        await adminCreateSurvey(form);
        showToast('Survey created');
      }
      setShowForm(false);
      load();
    } catch (e) { showToast('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function sendPending() {
    try { const r = await adminSendPendingSurveys(); showToast(`Sent ${r.sent} pending survey(s)`); load(); }
    catch (e) { showToast('Error: ' + e.message); }
  }

  return (
    <div style={s.page}>
      <AdminSidebar active="surveys" />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Surveys</h1>
            <p style={s.sub}>Build and send feedback surveys to clients after job completion.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={() => setShowHelp(true)} style={s.helpBtn} title="Survey builder guide">?</button>
            <button onClick={sendPending} style={s.secondaryBtn}>↑ Send Pending</button>
            <button onClick={openNew} style={s.primaryBtn}>+ New Survey</button>
          </div>
        </div>

        {showForm && (
          <div style={s.formCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={s.formTitle}>{editId ? 'Edit Survey' : 'New Survey'}</h2>
              <button onClick={() => setShowHelp(true)} style={s.helpBtnSm} title="Survey builder guide">? Help</button>
            </div>
            <div style={s.formGrid}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={s.label}>Title</label>
                <input style={s.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Post-Enrichment Feedback" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={s.label}>Description (shown to respondent)</label>
                <textarea style={{ ...s.input, height: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional intro text…" />
              </div>
              <div>
                <label style={s.label}>Send X days after job completes</label>
                <input style={s.input} type="number" min="0" max="90" value={form.trigger_days} onChange={e => setForm(f => ({ ...f, trigger_days: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={s.label}>Status</label>
                <select style={s.input} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ ...s.label, margin: 0 }}>Questions</label>
                <button onClick={() => setForm(f => ({ ...f, questions: [...f.questions, { ...BLANK_Q }] }))} style={s.addQBtn}>+ Add Question</button>
              </div>
              {form.questions.map((q, i) => (
                <QuestionRow key={i} q={q} idx={i} onChange={changeQ} onRemove={removeQ} onMove={moveQ} total={form.questions.length} />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={s.cancelBtn}>Cancel</button>
              <button onClick={save} disabled={saving} style={s.primaryBtn}>{saving ? 'Saving…' : 'Save Survey'}</button>
            </div>
          </div>
        )}

        {loading ? <div style={s.empty}>Loading…</div> : (
          <div style={s.tableCard}>
            {surveys.length === 0 ? <div style={s.empty}>No surveys yet. Create one above.</div> : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Title</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Trigger</th>
                    <th style={s.th}>Questions</th>
                    <th style={s.th}>Sent</th>
                    <th style={s.th}>Completed</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map(sv => (
                    <tr key={sv.survey_id} style={s.tr}>
                      <td style={s.td}><span style={{ fontWeight: 600 }}>{sv.title}</span></td>
                      <td style={s.td}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
                          color: sv.status === 'active' ? '#059669' : '#6b7280',
                          background: sv.status === 'active' ? 'rgba(5,150,105,0.08)' : 'rgba(107,114,128,0.08)' }}>
                          {sv.status}
                        </span>
                      </td>
                      <td style={s.td}>{sv.trigger_days}d after completion</td>
                      <td style={s.td}>{sv.question_count}</td>
                      <td style={s.td}>{sv.send_count}</td>
                      <td style={s.td}>{sv.response_count}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={s.viewBtn} onClick={() => navigate(`/admin/surveys/${sv.survey_id}/results`)}>Results</button>
                          <button style={s.viewBtn} onClick={() => window.open(`/admin/surveys/${sv.survey_id}/preview`, '_blank')}>Preview</button>
                          <button style={s.viewBtn} onClick={() => navigate(`/admin/surveys/${sv.survey_id}/send`)}>Send</button>
                          <button style={s.viewBtn} onClick={() => openEdit(sv)}>Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
      {toast && <div style={s.toast}>{toast}</div>}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}

const s = {
  page:        { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  main:        { flex: 1, padding: '36px 40px', overflowY: 'auto' },
  header:      { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title:       { fontSize: 24, fontWeight: 700, color: '#0a2540', margin: 0 },
  sub:         { fontSize: 13, color: '#6b7280', marginTop: 4 },
  primaryBtn:  { background: '#0176CE', border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '9px 18px' },
  secondaryBtn:{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 13, padding: '9px 16px' },
  cancelBtn:   { background: '#f1f5f9', border: 'none', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 13, padding: '9px 18px' },
  helpBtn:     { width: 32, height: 32, borderRadius: '50%', border: '2px solid #e2e8f0', background: '#fff', color: '#6b7280', cursor: 'pointer', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  helpBtnSm:   { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, color: '#6b7280', cursor: 'pointer', fontSize: 12, padding: '5px 12px' },
  addQBtn:     { background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 12, padding: '6px 14px' },
  formCard:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '28px 32px', marginBottom: 24 },
  formTitle:   { fontSize: 17, fontWeight: 700, color: '#0a2540', margin: 0 },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' },
  label:       { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
  input:       { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, color: '#374151', boxSizing: 'border-box', background: '#fff' },
  qRow:        { display: 'flex', gap: 12, alignItems: 'flex-start', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 8 },
  qMeta:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  qNum:        { width: 24, height: 24, background: '#0176CE', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 },
  qArrows:     { display: 'flex', flexDirection: 'column', gap: 2 },
  arrowBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 10, padding: '1px 4px', lineHeight: 1 },
  qInput:      { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#374151', boxSizing: 'border-box' },
  qSelect:     { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, color: '#374151', background: '#fff', flexShrink: 0 },
  toggleBtn:   { border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 11, padding: '4px 10px' },
  removeBtn:   { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: '4px 8px', lineHeight: 1 },
  tableCard:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' },
  tr:          { borderBottom: '1px solid #f1f5f9' },
  td:          { padding: '12px 16px', fontSize: 13, color: '#374151', verticalAlign: 'middle' },
  viewBtn:     { background: '#f1f5f9', border: 'none', borderRadius: 5, color: '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '5px 10px' },
  empty:       { color: '#9ca3af', padding: '40px', textAlign: 'center' },
  toast:       { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#0a2540', color: '#fff', padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 500, zIndex: 1000, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
  modalOverlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modalBox:    { background: '#fff', borderRadius: 14, padding: '28px 32px', width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' },
};
